import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  UserIcon,
  BeakerIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { fetchApi } from '../../utils/api';

// TypeScript interfaces
interface Patient {
  id: string;
  firstName: string;    // ← Using camelCase like the API returns
  lastName: string;     // ← Using camelCase like the API returns
  email?: string;
  phone?: string;       // ← Using 'phone' like the API returns
}

interface TreatmentType {
  id: string;
  name: string;
  duration?: number;
  cost?: string;
}

interface Dentist {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface AppointmentFormSettings {
  patient_search_method: 'name' | 'phone' | 'email' | 'all';
  show_dentist_field: boolean;
  show_status_field: boolean;
  show_source_field: boolean;
  show_notes_field: boolean;
}

interface AppointmentFormData {
  patient_id: string;
  treatment_type_id: string;
  dentist_id: string;
  start_date: string;
  start_time: string;
  end_time: string;
  status: string;
  appointment_source: string;
  notes: string;
  mode: string;
}

interface DynamicAppointmentFormProps {
  formData: AppointmentFormData;
  onFormDataChange: (data: AppointmentFormData) => void;
  initialDate?: string;
  initialTime?: string;
  preselectedPatient?: { id: string; firstName: string; lastName: string } | null;
}

const DynamicAppointmentForm: React.FC<DynamicAppointmentFormProps> = ({
  formData,
  onFormDataChange,
  initialDate,
  initialTime,
  preselectedPatient
}) => {
  const { t } = useTranslation(['appointments']);
  const [formSettings, setFormSettings] = useState<AppointmentFormSettings>({
    patient_search_method: 'all',
    show_dentist_field: true,
    show_status_field: true,
    show_source_field: false,
    show_notes_field: true
  });
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [treatmentTypes, setTreatmentTypes] = useState<TreatmentType[]>([]);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Load form settings and data when component mounts
  useEffect(() => {
    loadFormSettings();
    loadFormData();
  }, []);

  // Update form data with initial values
  useEffect(() => {
    if (initialDate || initialTime) {
      onFormDataChange({
        ...formData,
        start_date: initialDate || formData.start_date,
        start_time: initialTime || formData.start_time,
      });
    }
  }, [initialDate, initialTime]);

  // Set patient search query if a patient is preselected
  useEffect(() => {
    if (preselectedPatient) {
      const patientName = `${preselectedPatient.firstName || ''} ${preselectedPatient.lastName || ''}`.trim();
      setPatientSearchQuery(patientName);
      setSelectedPatient({
        id: preselectedPatient.id,
        firstName: preselectedPatient.firstName,
        lastName: preselectedPatient.lastName
      });
    }
  }, [preselectedPatient]);

  // Auto-calculate end time when treatment type changes
  useEffect(() => {
    if (formData.treatment_type_id && formData.start_time) {
      const selectedTreatment = treatmentTypes.find(t => t.id === formData.treatment_type_id);
      if (selectedTreatment?.duration) {
        const startDateTime = new Date(`2000-01-01T${formData.start_time}:00`);
        const endDateTime = new Date(startDateTime.getTime() + selectedTreatment.duration * 60000);
        const endTime = endDateTime.toTimeString().slice(0, 5);
        onFormDataChange({ ...formData, end_time: endTime });
      }
    }
  }, [formData.treatment_type_id, formData.start_time, treatmentTypes]);

  // Filter patients based on search query and settings
  useEffect(() => {
    if (!patientSearchQuery.trim()) {
      setFilteredPatients([]);
      return;
    }

    const query = patientSearchQuery.toLowerCase();
    const filtered = patients.filter(patient => {
      switch (formSettings.patient_search_method) {
        case 'name':
          return (patient.firstName?.toLowerCase() || '').includes(query) ||
                 (patient.lastName?.toLowerCase() || '').includes(query);
        case 'phone':
          return (patient.phone || '').includes(query);
        case 'email':
          return (patient.email?.toLowerCase() || '').includes(query);
        case 'all':
        default:
          return (patient.firstName?.toLowerCase() || '').includes(query) ||
                 (patient.lastName?.toLowerCase() || '').includes(query) ||
                 (patient.email?.toLowerCase() || '').includes(query) ||
                 (patient.phone || '').includes(query);
      }
    });
    
    setFilteredPatients(filtered.slice(0, 10));
  }, [patientSearchQuery, patients, formSettings.patient_search_method]);

  const loadFormSettings = async () => {
    try {
      const response = await fetch('/api/appointments/api/form-settings/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          setFormSettings(data.results[0]);
        }
      }
    } catch (error) {
      console.error('Error loading form settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadFormData = async () => {
    try {
      // Load patients
      const response = await fetchApi('/patients/api/list/');
      if (response.success) {
        setPatients(response.patients || []);
      } else {
        setPatients([]);
      }

      // Load treatment types
      const treatmentTypesData = await fetchApi('appointments/treatment-types/');
      setTreatmentTypes(treatmentTypesData.treatment_types || []);

      // TODO: Load dentists from API when endpoint is available
      // For now using staff members from the clinic
      try {
        const staffResponse = await fetch('/api/appointments/staff/', {
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
        if (staffResponse.ok) {
          const staffData = await staffResponse.json();
          setDentists(staffData.staff || staffData.dentists || []);
        }
      } catch {
        // Staff endpoint may not exist yet - leave dentists empty
      }
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    onFormDataChange({ ...formData, patient_id: patient.id });
    setPatientSearchQuery(`${patient.firstName || ''} ${patient.lastName || ''}`.trim());
    setShowPatientDropdown(false);
  };

  const getSearchMethodPlaceholder = () => {
    switch (formSettings.patient_search_method) {
      case 'name': return t('modal.fields.searchByName');
      case 'phone': return t('modal.fields.searchByPhone');
      case 'email': return t('modal.fields.searchByEmail');
      case 'all': 
      default: return t('modal.fields.searchByAll');
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">{t('modal.messages.loadingFormSettings')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Patient Selection with Dynamic Search */}
      <div>
        <label className="text-sm font-medium text-card-foreground mb-2 flex items-center">
          <UserIcon className="h-4 w-4 mr-2" />
          {t('modal.fields.patient')} *
        </label>
        
        {selectedPatient ? (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                  {`${(selectedPatient.firstName || '').charAt(0)}${(selectedPatient.lastName || '').charAt(0)}`.toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {selectedPatient.firstName} {selectedPatient.lastName}
                  </h3>
                  {(selectedPatient.email || selectedPatient.phone) && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedPatient.email && (
                        <div className="flex items-center space-x-1">
                          <EnvelopeIcon className="h-3.5 w-3.5" />
                          <span>{selectedPatient.email}</span>
                        </div>
                      )}
                      {selectedPatient.phone && (
                        <div className="flex items-center space-x-1">
                          <PhoneIcon className="h-3.5 w-3.5" />
                          <span>{selectedPatient.phone}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedPatient(null);
                  setPatientSearchQuery('');
                  onFormDataChange({ ...formData, patient_id: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Change patient"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={patientSearchQuery}
                onChange={(e) => {
                  setPatientSearchQuery(e.target.value);
                  setShowPatientDropdown(true);
                }}
                onFocus={() => setShowPatientDropdown(true)}
                onBlur={() => {
                  // Delay hiding dropdown to allow click events to fire
                  setTimeout(() => setShowPatientDropdown(false), 100);
                }}
                placeholder={getSearchMethodPlaceholder()}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
              />
            </div>
            
            {/* Patient Search Results Dropdown */}
            {showPatientDropdown && filteredPatients.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handlePatientSelect(patient);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePatientSelect(patient);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0"
                  >
                    <div className="font-medium text-card-foreground">
                      {patient.firstName || ''} {patient.lastName || ''}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {/* Always show available contact info, like the patients page */}
                      {patient.email && <span>{patient.email}</span>}
                      {patient.phone && (
                        <span className={patient.email ? ' • ' : ''}>{patient.phone}</span>
                      )}
                      {!patient.email && !patient.phone && (
                        <span className="text-muted-foreground/70">{t('modal.messages.noContactInfo')}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t('modal.fields.searchAllFields')}
            </p>
          </div>
        )}
      </div>

      {/* Treatment Type Selection */}
      <div>
        <label className="text-sm font-medium text-card-foreground mb-2 flex items-center">
          <BeakerIcon className="h-4 w-4 mr-2" />
          {t('modal.fields.treatmentType')}
        </label>
        <select
          value={formData.treatment_type_id}
          onChange={(e) => onFormDataChange({ ...formData, treatment_type_id: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="">{t('modal.fields.selectTreatment')}</option>
          {treatmentTypes.map((treatment) => (
            <option key={treatment.id} value={treatment.id}>
              {treatment.name} {treatment.duration && `(${treatment.duration} min)`} {treatment.cost && `- $${treatment.cost}`}
            </option>
          ))}
        </select>
      </div>

      {/* Conditional Dentist Field */}
      {formSettings.show_dentist_field && (
        <div>
          <label className="text-sm font-medium text-card-foreground mb-2 flex items-center">
            <UserCircleIcon className="h-4 w-4 mr-2" />
            {t('modal.fields.dentist')}
          </label>
          <select
            value={formData.dentist_id}
            onChange={(e) => onFormDataChange({ ...formData, dentist_id: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            <option value="">{t('modal.fields.selectDentist')}</option>
            {dentists.map((dentist) => (
              <option key={dentist.id} value={dentist.id}>
                {dentist.first_name} {dentist.last_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Date and Time */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            {t('modal.fields.date')}
          </label>
          <input
            type="date"
            required
            value={formData.start_date}
            onChange={(e) => onFormDataChange({ ...formData, start_date: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            {t('modal.fields.startTime')}
          </label>
          <input
            type="time"
            required
            value={formData.start_time}
            onChange={(e) => onFormDataChange({ ...formData, start_time: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            {t('modal.fields.endTime')}
          </label>
          <input
            type="time"
            value={formData.end_time}
            onChange={(e) => onFormDataChange({ ...formData, end_time: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>

      {/* Conditional Status and Source Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {formSettings.show_status_field && (
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              {t('modal.fields.status')}
            </label>
            <select
              value={formData.status}
              onChange={(e) => onFormDataChange({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="Scheduled">{t('status.scheduled')}</option>
              <option value="Completed">{t('status.completed')}</option>
              <option value="Cancelled">{t('status.cancelled')}</option>
              <option value="No-show">{t('status.noShow')}</option>
            </select>
          </div>
        )}

        {formSettings.show_source_field && (
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              {t('modal.fields.source')}
            </label>
            <select
              value={formData.appointment_source}
              onChange={(e) => onFormDataChange({ ...formData, appointment_source: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="Online">{t('modal.fields.sourceOptions.online')}</option>
              <option value="Phone">{t('modal.fields.sourceOptions.phone')}</option>
              <option value="In-Person">{t('modal.fields.sourceOptions.inPerson')}</option>
              <option value="Emergency">{t('modal.fields.sourceOptions.emergency')}</option>
              <option value="Referral">{t('modal.fields.sourceOptions.referral')}</option>
            </select>
          </div>
        )}
      </div>

      {/* Conditional Notes Field */}
      {formSettings.show_notes_field && (
        <div>
          <label className="flex items-center text-sm font-medium text-card-foreground mb-2">
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
            {t('modal.fields.notes')}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder={t('modal.fields.notesPlaceholder')}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      )}

      {/* Form Settings Info */}
      <div className="bg-muted/30 rounded-lg p-3">
        <div className="flex items-center text-sm text-muted-foreground">
          <CogIcon className="h-4 w-4 mr-2" />
          <span>
            {t('modal.messages.formConfigured', {
              method: formSettings.patient_search_method,
              count: [
                formSettings.show_dentist_field && 'dentist',
                formSettings.show_status_field && 'status',
                formSettings.show_source_field && 'source',
                formSettings.show_notes_field && 'notes'
              ].filter(Boolean).length
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DynamicAppointmentForm;
